import { useUserStore } from "@/stores/modules/user";
import {
  type CardBasic,
  type CardDeck,
  type PveStageConfig,
} from "@/types/pvp-card";
import { Client, Room } from "colyseus.js";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { ElMessage } from "element-plus";
import type {
  Card,
  GameState,
  GameStateJSON,
  Player,
} from "awakening-of-war-shared";
import { debounce, omit } from "lodash";
import { useCommonStore } from "@/stores/modules/common";
import { showNotice } from "./compose";
import { CARD_MAX_LEVEL, CardTypeIcon } from "./enum";
import { usePvpCardProfileStore } from "./gameProfile";
const SERVER_URL = import.meta.env.VITE_PVP_CARD_GAME_SERVER;

interface PveBattleResult {
  coin: number;
  diamond: number;
  cards: [number, number][];
  reason: string;
}
export const usePvpCardGameStore = defineStore("pvp-card-game", () => {
  const userStore = useUserStore();
  const commonStore = useCommonStore();
  const pvpCardProfileStore = usePvpCardProfileStore();
  let gameClient: Client;

  const gameRoom = ref<Room<GameState>>();
  const gameState = ref<GameStateJSON>({} as GameStateJSON);
  const cardDeck = ref<CardDeck>([]);
  const state = ref<"idle" | "ready" | "battle" | "connect">("connect");
  const isWaiting = computed(() => state.value === "ready");
  const countdownTime = ref(0);
  const gameTime = ref(0);
  const cardData = ref<Record<string, CardBasic>>({});
  const allStages = ref<PveStageConfig[]>([]);
  const battleResult = ref<any>(null);
  const pveBattleResult = ref<PveBattleResult | null>(null);
  const mode = ref<"standard" | "pve">("standard");
  // ===== 新增：大厅房间引用 =====
  const lobbyRoom = ref<Room>();

  function isLobbyConnected() {
    if (!lobbyRoom.value) return false;
    // @ts-ignore: connection 是私有属性，但实际可用
    const conn = lobbyRoom.value.connection;
    return conn && conn.isOpen;
  }
  // ===== 1. 连接大厅 =====
  const connectLobbyDebounce = debounce(connectLobby, 300);
  async function connectLobby(force = false) {
    if (!force && isLobbyConnected()) {
      return;
    }
    if (gameClient) {
      // 注意：Colyseus Client 没有显式的 dispose，但可以 leave 所有房间
      lobbyRoom.value?.leave();
      gameRoom.value?.leave();
    }
    gameClient = new Client(SERVER_URL);
    try {
      const room = await gameClient.joinOrCreate("lobby");
      lobbyRoom.value = room;

      // 接收卡牌数据
      room.onMessage("card-data", (data) => {
        cardData.value = data;
        state.value = "idle";
      });

      room.onMessage("stage-data", (data) => {
        allStages.value = data;
      });

      // ===== 监听匹配成功 =====
      room.onMessage("match-found", (roomInfo) => {
        joinGameRoom(roomInfo.roomId);
      });

      // 监听匹配错误
      room.onMessage("matchmaking-error", (msg) => {
        ElMessage.error(msg.message || "匹配失败");
        state.value = "idle";
      });

      // 监听匹配开始
      room.onMessage("matchmaking-started", (data) => {
        state.value = "ready";
        countdown();
        ElMessage.info(`${data.message}, 当前匹配: ${data.queueSize}人`);
      });

      room.onMessage("global-notice", (msg) => {
        if (state.value === "idle") {
          showNotice(msg);
        }
      });

      room.onMessage("pve-sweeping", (data) => {
        setPveBattleResult(data);
        pvpCardProfileStore.init();
      });
      room.onLeave((code) => {
        lobbyRoom.value = undefined;

        if ([1006, 1011].includes(code)) {
          // 非正常关闭（如网络中断、服务器重启）
          ElMessage.warning("战魂对决连接中断，正在尝试重连...");
          connectLobbyDebounce(true); // force 重连
        }
      });

      const saved = localStorage.getItem("gameReconnect");
      if (saved) {
        const data = JSON.parse(saved);
        if (Date.now() - data.timestamp < 180_000) {
          reconnectGameRoom(data.reconnectToken);
        } else {
          localStorage.removeItem("gameReconnect");
        }
      }
    } catch (error) {
      ElMessage.error("连接战魂对决失败");
      commonStore.path = "Pub";
    }
  }
  async function disconnectLobby() {
    lobbyRoom.value?.leave();
    gameRoom.value?.leave();
  }
  // ===== 2. 发起匹配请求 =====
  async function startMatchmaking() {
    try {
      if (!isLobbyConnected()) {
        await connectLobby(true);
      }

      if (!lobbyRoom.value) {
        ElMessage.warning("未连接大厅，请稍后重试");
        return;
      }
      // 发送匹配请求
      lobbyRoom.value.send("start-matchmaking", { token: userStore.token });
    } catch (error) {
      console.error("发起匹配失败:", error);
      ElMessage.error("发起匹配失败");
    }
  }

  // ===== 3. 取消匹配 =====
  async function cancelMatchmaking() {
    if (lobbyRoom.value) {
      lobbyRoom.value.send("cancel-matchmaking");
    }
    if (state.value === "ready") {
      state.value = "idle";
      ElMessage.info("已取消匹配");
    }
  }

  // ===== 4. 加入对战房间（由服务端通知后调用）=====
  async function joinGameRoom(roomId: string) {
    try {
      const room = await gameClient.joinById<GameState>(roomId, {
        name: userStore.nickname,
        token: userStore.token,
      });
      localStorage.setItem(
        "gameReconnect",
        JSON.stringify({
          reconnectToken: room.reconnectionToken,
          timestamp: Date.now(),
        })
      );
      gameRoom.value = room;
      setupRoomListeners(room);
    } catch (error) {
      console.error("加入对战房间失败:", error);
      ElMessage.error("加入对战房间失败");
      state.value = "idle";
    }
  }
  async function reconnectGameRoom(reconnectToken: string) {
    try {
      const room = await gameClient.reconnect<GameState>(reconnectToken);
      localStorage.removeItem("gameReconnect");
      gameRoom.value = room;
      setupRoomListeners(room);
    } catch (error) {
      console.error("重新连接对战房间失败:", error);
      // ElMessage.error("重新连接对战房间失败");
      state.value = "idle";
    }
  }
  function countdown(d = Date.now()) {
    const dt = Math.floor((Date.now() - d) / 1000);
    if (dt !== countdownTime.value) countdownTime.value = dt;
    if (isWaiting.value) {
      requestAnimationFrame(() => countdown(d));
    }
  }

  async function cancelGame() {
    cancelMatchmaking();
    gameRoom.value?.leave();
    gameRoom.value = void 0;
  }

  async function setupRoomListeners(room: Room<GameState>) {
    room.onLeave((code) => {
      if ([1006, 1011].includes(code)) {
        ElMessage.error("对战连接中断，返回大厅");
        exitBattle();
        connectLobbyDebounce(true);
      }
    });
    room.onMessage("player-joined", () => {
      state.value = "ready";
    });
    room.onMessage(
      "ready-check",
      (data: {
        gameTime: number;
        cardDeck: CardDeck;
        mode: typeof mode.value;
      }) => {
        gameTime.value = data.gameTime;
        cardDeck.value = data.cardDeck;
        state.value = "battle";
        mode.value = data.mode;
      }
    );
    room.onMessage(
      "reconnected",
      (data: {
        gameTime: number;
        state: GameStateJSON;
        cardDeck: CardDeck;
        mode: typeof mode.value;
      }) => {
        gameTime.value = data.gameTime;
        gameState.value = data.state;
        state.value = "battle";
        cardDeck.value = data.cardDeck;
        mode.value = data.mode;
      }
    );
    // room.onMessage("player-left", () => {
    //   state.value = "idle";
    // });
    room.onMessage("game-ended", (data) => {
      if (mode.value === "standard") {
        battleResult.value = {
          isWin: data.token === userStore.token,
          gain: data.gain,
          gain2: data.gain2,
          winnerCardPacks: data.winnerCardPacks,
          isTie: !data.token, // 假设无 token 表示平局
        };
      } else {
        setPveBattleResult(data);
      }
      cancelGame();
      exitBattle();
    });

    room.onMessage("error", (msg) => {
      ElMessage.warning(msg.message);
    });

    let timer: number;

    room.onStateChange((state) => {
      gameState.value = state.toJSON();
      clearTimeout(timer);
      timer = window.setTimeout(() => {
        const saved = localStorage.getItem("gameReconnect");
        if (saved) {
          const data = JSON.parse(saved);
          reconnectGameRoom(data.reconnectToken);
        }
      }, 3000);
    });
    room.onMessage("card-attacked", (data) => {
      attackAnimation.value = {
        attackerId: data.attackerId,
        targetId: data.targetId,
        timestamp: Date.now(),
      };
    });
    room.onMessage("animation-trigger", (payload) => {
      const {
        type,
        targets = [],
        center,
        primaryTarget,
        chainTargets = [],
      } = payload;

      const applyEffect = (
        cardId: string,
        effectClass: string,
        duration: number
      ) => {
        const el = document.querySelector(`.chess[data-card-id="${cardId}"]`);
        if (!el) return;
        el.classList.add(effectClass);
        setTimeout(() => el.classList.remove(effectClass), duration);
      };

      switch (type) {
        case "lightning":
          applyEffect(primaryTarget, "lightning", 400);
          chainTargets.forEach((id: string, i: number) => {
            setTimeout(() => applyEffect(id, "lightning", 400), i * 150);
          });
          break;

        case "aoe":
          targets.forEach((id: string) => {
            applyEffect(id, "aoe-hit", 600);
          });
          break;

        case "explosion":
          {
            const index = Math.round(center.x + center.y * 15 + 1);
            const el = document.querySelector(`.grid-item:nth-child(${index})`);
            if (el) {
              el.classList.add("exploded");
              setTimeout(() => el.classList.remove("exploded"), 700);
            }
            targets.forEach((id: string) => {
              applyEffect(id, "aoe-hit", 600);
            });
          }
          break;
        case "explosion-ice":
          {
            const index = Math.round(center.x + center.y * 15 + 1);
            const el = document.querySelector(`.grid-item:nth-child(${index})`);
            if (el) {
              el.classList.add("exploded-ice");
              setTimeout(() => el.classList.remove("exploded-ice"), 700);
            }
            targets.forEach((id: string) => {
              applyEffect(id, "aoe-hit", 600);
            });
          }
          break;
      }
    });
    room.onMessage("game-step", (data) => {
      gameStep.value = data.gameStep;
      if (data.gameStep === 2) {
        ElMessage.warning("双倍金币阶段！");
      } else if (data.gameStep === 3) {
        ElMessage.warning("死亡烈焰阶段！");
      }
    });
  }
  const gameStep = ref(1);
  const attackAnimation = ref<{
    attackerId: string;
    targetId: string;
    timestamp: number;
  } | void>();

  function useCard(card: number, index: number) {
    gameRoom.value?.send("use-card", { card, index });
  }

  function getDetailById(id: number) {
    return cardData.value[id];
  }

  function exitBattle() {
    initState();
  }
  function initState() {
    state.value = "idle";
    countdownTime.value = 0;
    gameTime.value = 0;
    gameState.value = {} as GameStateJSON;
    gameStep.value = 1;
    localStorage.removeItem("gameReconnect");
  }
  function clearBattleResult() {
    battleResult.value = null;
  }
  function clearPveBattleResult() {
    pveBattleResult.value = null;
  }
  function sendNotice(msg: string) {
    lobbyRoom.value?.send("global-notice", msg);
  }

  const playerByDir = computed(() => {
    const playerDir: Record<string, Player> = {};
    for (const playerId in gameState.value.players) {
      const player = gameState.value.players[playerId];
      playerDir[player.dir] = player;
    }
    return playerDir;
  });

  function getCardLevel(card: Card) {
    let level;
    if ([1000, 2000, 3000, 4000].includes(card.cardId) || card.isBoss) {
      level = playerByDir.value[card.dir].level;
    } else {
      level =
        cardDeck.value
          .find((item) => item.dir === card.dir)
          ?.deck.find((item) => item[0] === card.cardId)?.[1] || 1;
    }
    return Math.min(level, CARD_MAX_LEVEL);
  }
  function getCardTypeIcon(card: Card) {
    return CardTypeIcon[cardData.value[card.cardId].type];
  }

  // pve
  // ===== 新增：开始 PvE 战斗 =====
  async function startPveBattle(stageId: number) {
    try {
      // 确保已连接 Client（可复用 gameClient）
      if (!gameClient) {
        gameClient = new Client(SERVER_URL);
      }

      // 直接创建 PvE 房间（不经过 lobby）
      const room = await gameClient.create<GameState>("game", {
        mode: "pve",
        stageId,
        name: userStore.nickname,
        token: userStore.token,
      });

      localStorage.setItem(
        "gameReconnect",
        JSON.stringify({
          reconnectToken: room.reconnectionToken,
          timestamp: Date.now(),
        })
      );

      gameRoom.value = room;
      setupRoomListeners(room);
    } catch (error) {
      console.error("创建 PvE 房间失败:", error);
      ElMessage.error("无法开始 PvE 战斗");
      state.value = "idle";
    }
  }
  async function sweepPve(stageId: number, amount: number) {
    lobbyRoom.value?.send("pve-sweeping", {
      token: userStore.token,
      stageId,
      amount,
    });
  }

  function setPveBattleResult(result: PveBattleResult) {
    pveBattleResult.value = result;
    if (result.coin) {
      userStore.consumables.duelCoin += result.coin;
    }
    if (result.diamond) {
      userStore.consumables.duelDiamond += result.diamond;
    }
  }
  return {
    gameRoom,
    cancelGame,
    isWaiting,
    state,
    countdownTime,
    gameState,
    useCard,
    gameTime,
    cardData,
    getDetailById,
    attackAnimation,
    startMatchmaking,
    cancelMatchmaking,
    exitBattle,
    connectLobby,
    disconnectLobby,
    battleResult,
    clearBattleResult,
    gameStep,
    sendNotice,
    getCardLevel,
    getCardTypeIcon,
    // pve
    mode,
    allStages,
    startPveBattle,
    pveBattleResult,
    clearPveBattleResult,
    sweepPve,
  };
});
